import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Instagram, Linkedin, Facebook, Youtube, Send, Calendar } from "lucide-react";
import { SOCIAL } from "../../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  "Under $2,500 AUD",
  "$2,500 – $7,500 AUD",
  "$7,500 – $20,000 AUD",
  "$20,000 – $50,000 AUD",
  "$50,000+ AUD",
  "Not sure yet — need guidance",
];

const TIMELINES = [
  "ASAP / this month",
  "1–3 months",
  "3–6 months",
  "6+ months",
  "Flexible / exploratory",
];

const SCOPE_PROMPT = `Give me a snapshot of what you're working on. A few helpful things to include:
• What you're building or trying to change
• Who it's for (audience, users, team)
• The outcome you're hoping to reach
• Anything already tried, and what's blocking you now`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    topic: "", budget: "", timeline: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. I'll be in touch within 2 business days.");
      setForm({ name: "", email: "", company: "", topic: "", budget: "", timeline: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again or email me directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ label, children }) => (
    <div>
      <label className="block text-sm font-semibold text-navy uppercase tracking-[0.14em] mb-2">
        {label}
      </label>
      {children}
    </div>
  );

  const inputCls = "w-full px-4 py-3.5 bg-cream text-navy border-2 border-[#E5E0D8] focus:border-terracotta focus:outline-none transition-colors text-base";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-cream"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="eyebrow" data-testid="contact-eyebrow">Start a project</span>
          <h2
            data-testid="contact-heading"
            className="font-display text-4xl md:text-5xl lg:text-[56px] leading-[1.05] mt-6 text-navy"
          >
            Tell me about
            <br />
            <span className="italic text-terracotta">your project.</span>
          </h2>
          <p className="mt-6 text-lg text-ink-muted max-w-md leading-relaxed">
            The more detail you share, the sharper the plan I'll bring to our first call.
            I read every message myself and reply within 2 business days.
          </p>

          <div className="mt-8 p-6 border border-[#E5E0D8] bg-cream-alt">
            <div className="flex items-start gap-3">
              <Calendar size={20} className="text-terracotta flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-navy">Prefer to book a call?</div>
                <p className="text-[15px] text-ink-muted mt-1">
                  Once I receive your enquiry I'll send a link to my calendar to book a
                  15-min discovery call at a time that suits you.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 space-y-5">
            <a
              href={`mailto:${SOCIAL.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 bg-navy text-cream flex items-center justify-center group-hover:bg-terracotta transition-colors">
                <Mail size={18} />
              </span>
              <span className="text-navy text-lg group-hover:text-terracotta transition-colors">
                {SOCIAL.email}
              </span>
            </a>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="w-11 h-11 border border-navy/40 flex items-center justify-center text-navy hover:bg-navy hover:text-cream transition-colors"><Instagram size={18} /></a>
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" aria-label="LinkedIn" className="w-11 h-11 border border-navy/40 flex items-center justify-center text-navy hover:bg-navy hover:text-cream transition-colors"><Linkedin size={18} /></a>
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" aria-label="Facebook" className="w-11 h-11 border border-navy/40 flex items-center justify-center text-navy hover:bg-navy hover:text-cream transition-colors"><Facebook size={18} /></a>
              <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" data-testid="contact-youtube" aria-label="YouTube" className="w-11 h-11 border border-navy/40 flex items-center justify-center text-navy hover:bg-navy hover:text-cream transition-colors"><Youtube size={18} /></a>
              <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok" aria-label="TikTok" className="w-11 h-11 border border-navy/40 flex items-center justify-center text-navy hover:bg-navy hover:text-cream transition-colors font-bold text-[13px] tracking-wider">TT</a>
              <a href={SOCIAL.pinterest} target="_blank" rel="noopener noreferrer" data-testid="contact-pinterest" aria-label="Pinterest" className="w-11 h-11 border border-navy/40 flex items-center justify-center text-navy hover:bg-navy hover:text-cream transition-colors font-display italic text-lg">P</a>
              <a href={SOCIAL.coroflot} target="_blank" rel="noopener noreferrer" data-testid="contact-coroflot" aria-label="Coroflot" className="h-11 px-3 border border-navy/40 flex items-center justify-center text-navy hover:bg-navy hover:text-cream transition-colors text-[11px] uppercase tracking-[0.16em] font-semibold">Coroflot</a>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-cream-alt p-8 md:p-10 border border-[#E5E0D8]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name">
              <input type="text" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="contact-name-input" className={inputCls} />
            </Field>
            <Field label="Email">
              <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-email-input" className={inputCls} />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Company / brand (optional)">
              <input type="text" value={form.company} onChange={(e) => update("company", e.target.value)} data-testid="contact-company-input" className={inputCls} placeholder="Your business or brand name" />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="What's it about?">
              <select value={form.topic} onChange={(e) => update("topic", e.target.value)} data-testid="contact-topic-select" required className={inputCls}>
                <option value="">Select a service…</option>
                <option value="Brand Development">Brand Development</option>
                <option value="C-Level Consulting">C-Level Consulting</option>
                <option value="Product Design">Product Design / Tech</option>
                <option value="Modeling">Modeling Enquiry</option>
                <option value="Brand Partnership / UGC">Brand Partnership / UGC</option>
                <option value="Speaking / Advocacy">Speaking / Advocacy</option>
                <option value="Other">Other</option>
              </select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <Field label="Estimated budget">
              <select value={form.budget} onChange={(e) => update("budget", e.target.value)} data-testid="contact-budget-select" className={inputCls}>
                <option value="">Select budget range…</option>
                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Timeline">
              <select value={form.timeline} onChange={(e) => update("timeline", e.target.value)} data-testid="contact-timeline-select" className={inputCls}>
                <option value="">Select timeline…</option>
                {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Project scope & goals">
              <textarea
                required
                rows={7}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                data-testid="contact-message-input"
                className={`${inputCls} resize-none`}
                placeholder={SCOPE_PROMPT}
              />
            </Field>
            <p className="mt-2 text-xs text-ink-muted">
              The more context you share here, the more tailored my first response will be.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="btn-primary mt-7 w-full sm:w-auto justify-center"
          >
            {loading ? "Sending…" : (<>Send enquiry <Send size={18} /></>)}
          </button>
        </form>
      </div>
    </section>
  );
}
